import React, { useState, ChangeEvent, DragEvent } from 'react';
import { Download, Info } from 'lucide-react';
import axios, { AxiosError } from 'axios';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL || "https://alliedge.com/mcqwale";
// const BACKEND_URL = "http://localhost:8000/mcqwale";

const AddLiveMcqExcel: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [showFormatGuide, setShowFormatGuide] = useState<boolean>(false);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (selectedFile: File | undefined) => {
    if (selectedFile?.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
      setFile(selectedFile);
      setError("");
      setShowModal(true);
    } else {
      setFile(null);
      setError("Please upload a valid .xlsx file.");
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError("No file selected. Please upload an .xlsx file.");
      return;
    }

    const reader = new FileReader();

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      try {
        const result = e.target?.result;
        if (typeof result !== 'string') {
          throw new Error('Failed to read file as string');
        }
        
        const base64String = result.split(",")[1];
        const response = await axios.post(
          `${BACKEND_URL}/live-mcq/upload-excel`,
          { file: base64String },
          { headers: { "Content-Type": "application/json" } }
        );

        setSuccessMessage(response.data.message || "File uploaded successfully!");
        setError("");
        setShowModal(false);
        setFile(null);
      } catch (err) {
        const axiosError = err as AxiosError<{ message: string }>;
        setError(axiosError.response?.data?.message || "An error occurred while uploading the file.");
        setSuccessMessage("");
      }
    };

    reader.onerror = () => {
      setError("Failed to read the file. Please try again.");
    };

    reader.readAsDataURL(file);
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-2xl">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Upload Excel File</h2>
          <p className="text-gray-600">Upload your questions in Excel format (.xlsx)</p>
        </div>

        <button
          onClick={() => setShowFormatGuide(!showFormatGuide)}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
        >
          <Info size={20} />
          <span>View Required Format</span>
        </button>

        {showFormatGuide && (
          <div className="mb-6 bg-blue-50 rounded-lg overflow-hidden">
            <div className="p-4 bg-blue-100">
              <h3 className="font-semibold text-lg text-blue-800">Required Excel Format</h3>
              <p className="text-sm text-blue-600">Your Excel file should match this format exactly:</p>
            </div>
            <div className="p-4">
              <img 
                src="https://res.cloudinary.com/dlgq7m52a/image/upload/v1738913264/ir6l2anbkexywvcwpxrp.png" 
                alt="Excel Format Example"
                className="w-full h-32 rounded-lg shadow-sm"
              />
              
            </div>
          </div>
        )}

        <a
        href='Format.xlsx'
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
        >
          <Download size={20} />
          <span>Download Required Format</span>
        </a>

        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center ${
            isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div className="space-y-2">
            <div className="flex justify-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
            </div>
            <div className="text-gray-600">
              <span className="font-medium">Click to upload</span> or drag and drop
            </div>
            <p className="text-sm text-gray-500">.xlsx files only</p>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {successMessage}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full">
              <h3 className="text-xl font-bold mb-4">Upload Confirmation</h3>
              <p className="text-gray-600 mb-4">
                Selected file: <span className="font-medium">{file?.name}</span>
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setFile(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Upload File
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddLiveMcqExcel;